import React, { useState, useCallback } from 'react';

import classNames from 'classnames';
import { useMutation, useQuery, useQueryCache } from 'react-query';
import { Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import {
  fetchOfficeSchedules,
  createOfficeSchedule,
  updateOfficeSchedule,
  deleteOfficeSchedule,
} from '../api';
import type { OfficeSchedule, TimeSlot, CreateOfficeSchedulePayload } from '../api';

interface OfficeScheduleManagerProps {
  accountId: number;
}

const DAYS_OF_WEEK = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const DAY_LABELS: Record<string, string> = {
  mon: 'Lunes',
  tue: 'Martes',
  wed: 'Miércoles',
  thu: 'Jueves',
  fri: 'Viernes',
  sat: 'Sábado',
  sun: 'Domingo',
};

const DEFAULT_TIME_SLOTS: TimeSlot[] = DAYS_OF_WEEK.map((day) => ({
  day_of_week: day,
  is_open: day !== 'sat' && day !== 'sun',
  open_time: day !== 'sat' && day !== 'sun' ? '08:00' : null,
  close_time: day !== 'sat' && day !== 'sun' ? '17:00' : null,
}));

interface ScheduleFormState {
  name: string;
  time_slots: TimeSlot[];
}

const TimeSlotRow: React.FC<{
  slot: TimeSlot;
  onChange: (slot: TimeSlot) => void;
}> = ({ slot, onChange }) => {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-white-2.0 last:border-b-0">
      <span className="w-24 text-sm font-medium text-black-1.0">
        {DAY_LABELS[slot.day_of_week]}
      </span>
      <button
        type="button"
        onClick={() =>
          onChange({
            ...slot,
            is_open: !slot.is_open,
            open_time: !slot.is_open ? '08:00' : null,
            close_time: !slot.is_open ? '17:00' : null,
          })
        }
        className={classNames(
          'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200',
          slot.is_open ? 'bg-primary-2.0' : 'bg-gray-1.0'
        )}
      >
        <span
          className={classNames(
            'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200',
            slot.is_open ? 'translate-x-4' : 'translate-x-0'
          )}
        />
      </button>
      {slot.is_open ? (
        <div className="flex items-center gap-2">
          <input
            type="time"
            value={slot.open_time ?? '08:00'}
            onChange={(e) => onChange({ ...slot, open_time: e.target.value })}
            className="rounded border border-white-2.0 px-2 py-1 text-sm"
          />
          <span className="text-gray-2.0">-</span>
          <input
            type="time"
            value={slot.close_time ?? '17:00'}
            onChange={(e) => onChange({ ...slot, close_time: e.target.value })}
            className="rounded border border-white-2.0 px-2 py-1 text-sm"
          />
        </div>
      ) : (
        <span className="text-sm text-gray-2.0">Cerrado</span>
      )}
    </div>
  );
};

export const OfficeScheduleManager: React.FC<OfficeScheduleManagerProps> = ({ accountId }) => {
  const queryCache = useQueryCache();
  const [editingSchedule, setEditingSchedule] = useState<OfficeSchedule | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formState, setFormState] = useState<ScheduleFormState>({
    name: '',
    time_slots: [...DEFAULT_TIME_SLOTS],
  });

  const { data: schedules = [], isLoading } = useQuery(
    ['office-schedules', accountId],
    () => fetchOfficeSchedules(accountId),
    { refetchOnWindowFocus: false }
  );

  const resetForm = useCallback(() => {
    setFormState({ name: '', time_slots: [...DEFAULT_TIME_SLOTS] });
    setEditingSchedule(null);
    setShowCreateForm(false);
  }, []);

  const [createSchedule, { isLoading: isCreating }] = useMutation(
    (payload: CreateOfficeSchedulePayload) => createOfficeSchedule(accountId, payload),
    {
      onSuccess: () => {
        Toast.success('Horario creado exitosamente');
        queryCache.invalidateQueries(['office-schedules', accountId]);
        resetForm();
      },
      onError: () => {
        Toast.error('Error al crear el horario');
      },
    }
  );

  const [updateSchedule, { isLoading: isUpdating }] = useMutation(
    ({ id, payload }: { id: number; payload: CreateOfficeSchedulePayload }) =>
      updateOfficeSchedule(id, payload),
    {
      onSuccess: () => {
        Toast.success('Horario actualizado exitosamente');
        queryCache.invalidateQueries(['office-schedules', accountId]);
        resetForm();
      },
      onError: () => {
        Toast.error('Error al actualizar el horario');
      },
    }
  );

  const [removeSchedule] = useMutation((id: number) => deleteOfficeSchedule(id), {
    onSuccess: () => {
      Toast.success('Horario eliminado exitosamente');
      queryCache.invalidateQueries(['office-schedules', accountId]);
    },
    onError: () => {
      Toast.error('Error al eliminar el horario');
    },
  });

  const handleEdit = useCallback((schedule: OfficeSchedule) => {
    setEditingSchedule(schedule);
    setShowCreateForm(false);
    setFormState({
      name: schedule.name,
      time_slots: schedule.time_slots.map((s) => ({ ...s })),
    });
  }, []);

  const handleCreate = useCallback(() => {
    setShowCreateForm(true);
    setEditingSchedule(null);
    setFormState({ name: '', time_slots: [...DEFAULT_TIME_SLOTS] });
  }, []);

  const handleSlotChange = useCallback((index: number, slot: TimeSlot) => {
    setFormState((prev) => ({
      ...prev,
      time_slots: prev.time_slots.map((s, i) => (i === index ? slot : s)),
    }));
  }, []);

  const handleSave = useCallback(() => {
    const payload: CreateOfficeSchedulePayload = {
      name: formState.name,
      time_slots: formState.time_slots.map((s) => ({
        day_of_week: s.day_of_week,
        is_open: s.is_open,
        open_time: s.is_open ? s.open_time : null,
        close_time: s.is_open ? s.close_time : null,
      })),
    };

    if (editingSchedule) {
      updateSchedule({ id: editingSchedule.id, payload });
    } else {
      createSchedule(payload);
    }
  }, [formState, editingSchedule, createSchedule, updateSchedule]);

  const isFormValid = formState.name.trim().length > 0;
  const isSaving = isCreating || isUpdating;

  return (
    <div className="p-4">
      {isLoading && <p className="text-sm text-gray-2.0">Cargando horarios...</p>}

      {!isLoading && !showCreateForm && !editingSchedule && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-2.0">
              {schedules.length === 0
                ? 'No hay horarios configurados'
                : `${schedules.length} horario(s) configurado(s)`}
            </p>
            <button
              type="button"
              onClick={handleCreate}
              className="btn btn-secondary btn-sm flex items-center gap-1"
            >
              <Icon name={OrbitIcons.PlusCircle} size={14} />
              Crear horario
            </button>
          </div>

          <div className="space-y-2">
            {schedules.map((schedule) => (
              <div
                key={schedule.id}
                className="flex items-center justify-between rounded border border-white-2.0 bg-white-1.0 px-3 py-2"
              >
                <div>
                  <p className="text-sm font-semibold text-black-1.0">{schedule.name}</p>
                  <p className="text-xs text-gray-2.0">
                    {schedule.time_slots.filter((s) => s.is_open).length} días abiertos
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(schedule)}
                    className="p-1 text-gray-2.0 hover:text-primary-2.0"
                  >
                    <Icon name={OrbitIcons.Pencil} size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('¿Eliminar este horario?')) {
                        removeSchedule(schedule.id);
                      }
                    }}
                    className="p-1 text-gray-2.0 hover:text-red-500"
                  >
                    <Icon name={OrbitIcons.Trash} size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {(showCreateForm || editingSchedule) && (
        <div className="rounded border border-white-2.0 bg-white-0.0 p-4">
          <h4 className="mb-4 text-sm font-bold text-black-1.0">
            {editingSchedule ? 'Editar horario' : 'Nuevo horario'}
          </h4>

          <div className="mb-4">
            <label className="mb-1 block text-xs font-medium text-gray-3.0">
              Nombre del horario<span className="ml-0.5 text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Ej: Horario regular"
              value={formState.name}
              onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full rounded border border-white-2.0 px-3 py-2 text-sm"
            />
          </div>

          <div className="mb-4">
            <p className="mb-2 text-xs font-medium text-gray-3.0">Días y horarios</p>
            <div className="rounded border border-white-2.0 bg-white-1.0 px-3 py-1">
              {formState.time_slots.map((slot, index) => (
                <TimeSlotRow
                  key={slot.day_of_week}
                  slot={slot}
                  onChange={(s) => handleSlotChange(index, s)}
                />
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={resetForm} className="btn btn-ghost btn-sm">
              Cancelar
            </button>
            <button
              type="button"
              disabled={!isFormValid || isSaving}
              onClick={handleSave}
              className="btn btn-secondary btn-sm"
            >
              {isSaving ? 'Guardando...' : editingSchedule ? 'Guardar cambios' : 'Crear horario'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
